import { useState, useCallback } from 'react';

export interface PermissionsState {
	accessEntriesOwnonlyRead: boolean;
	accessEntriesOwnonlyWrite: boolean;
	accessEntriesOwnonlyDelete: boolean;
	accessAnalysisOwnonly: boolean;
	accessEntriesOrgRead: boolean;
	accessEntriesOrgWrite: boolean;
	accessEntriesOrgDelete: boolean;
	accessAnalysisOrg: boolean;
	accessEntriesAllRead: boolean;
	accessEntriesAllWrite: boolean;
	accessEntriesAllDelete: boolean;
	accessAnalysisAll: boolean;
	accessExport: boolean;
	accessPersonalData: boolean;
	accessVariablesWrite: boolean;
	accessProjectWrite: boolean;
	accessProjectRead: boolean;
	accessVariablesRead: boolean;
	projectDesign: boolean;
	accessCodelistRead: boolean;
	accessCodelistWrite: boolean;
	accessCodelistExport: boolean;
}

type PermissionFlag = keyof PermissionsState;

interface DependencyRule {
	requires?: PermissionFlag[];
	disablesOnTrue?: PermissionFlag[];
}

export const initialPermissionsState: PermissionsState = {
	accessEntriesOwnonlyRead: false,
	accessEntriesOwnonlyWrite: false,
	accessEntriesOwnonlyDelete: false,
	accessAnalysisOwnonly: false,
	accessEntriesOrgRead: false,
	accessEntriesOrgWrite: false,
	accessEntriesOrgDelete: false,
	accessAnalysisOrg: false,
	accessEntriesAllRead: false,
	accessEntriesAllWrite: false,
	accessEntriesAllDelete: false,
	accessAnalysisAll: false,
	accessExport: false,
	accessPersonalData: false,
	accessVariablesWrite: false,
	accessProjectWrite: false,
	accessProjectRead: false,
	accessVariablesRead: false,
	projectDesign: false,
	accessCodelistRead: false,
	accessCodelistWrite: false,
	accessCodelistExport: false
};

const BASE_READ_REQUIREMENTS: PermissionFlag[] = ['accessProjectRead', 'accessVariablesRead'];

export const permissionDependencies: Record<PermissionFlag, DependencyRule> = {
	accessEntriesOwnonlyRead: { requires: [...BASE_READ_REQUIREMENTS] },
	accessEntriesOwnonlyWrite: { requires: ['accessEntriesOwnonlyRead'] },
	accessEntriesOwnonlyDelete: { requires: ['accessEntriesOwnonlyRead'] },
	accessAnalysisOwnonly: {},
	accessEntriesOrgRead: {
		requires: ['accessEntriesOwnonlyRead'],
		disablesOnTrue: ['accessEntriesOwnonlyRead']
	},
	accessEntriesOrgWrite: {
		requires: ['accessEntriesOrgRead', 'accessEntriesOwnonlyWrite'],
		disablesOnTrue: ['accessEntriesOwnonlyWrite']
	},
	accessEntriesOrgDelete: {
		requires: ['accessEntriesOrgRead', 'accessEntriesOwnonlyDelete'],
		disablesOnTrue: ['accessEntriesOwnonlyDelete']
	},
	accessAnalysisOrg: {
		requires: ['accessAnalysisOwnonly'],
		disablesOnTrue: ['accessAnalysisOwnonly']
	},
	accessEntriesAllRead: {
		requires: ['accessEntriesOrgRead'],
		disablesOnTrue: ['accessEntriesOrgRead', 'accessEntriesOwnonlyRead']
	},
	accessEntriesAllWrite: {
		requires: ['accessEntriesAllRead', 'accessEntriesOrgWrite'],
		disablesOnTrue: ['accessEntriesOrgWrite', 'accessEntriesOwnonlyWrite']
	},
	accessEntriesAllDelete: {
		requires: ['accessEntriesAllRead', 'accessEntriesOrgDelete'],
		disablesOnTrue: ['accessEntriesOrgDelete', 'accessEntriesOwnonlyDelete']
	},
	accessAnalysisAll: {
		requires: ['accessAnalysisOrg'],
		disablesOnTrue: ['accessAnalysisOrg', 'accessAnalysisOwnonly']
	},
	accessExport: { requires: [...BASE_READ_REQUIREMENTS] },
	accessPersonalData: { requires: [...BASE_READ_REQUIREMENTS] },
	accessVariablesWrite: {
		requires: ['accessVariablesRead', 'projectDesign'],
		disablesOnTrue: ['accessVariablesRead']
	},
	accessProjectWrite: { requires: ['accessProjectRead'] },
	accessCodelistRead: {},
	accessCodelistWrite: {
		requires: ['accessCodelistRead'],
		disablesOnTrue: ['accessCodelistRead']
	},
	accessCodelistExport: {
		requires: ['accessCodelistRead'],
		disablesOnTrue: ['accessCodelistRead']
	},
	accessProjectRead: {},
	accessVariablesRead: {},
	projectDesign: {}
};

const sanitizePermissionsState = (state: PermissionsState): PermissionsState => {
	const newState = { ...state };
	let changesMadeInIteration = true;

	while (changesMadeInIteration) {
		changesMadeInIteration = false;
		for (const key in permissionDependencies) {
			const flag = key as PermissionFlag;

			if (!(flag in newState)) {
				continue;
			}

			if (newState[flag]) {
				const dependencies = permissionDependencies[flag]?.requires || [];
				for (const reqFlag of dependencies) {
					if (!(reqFlag in newState)) {
						continue;
					}
					if (!newState[reqFlag]) {
						newState[reqFlag] = true;
						changesMadeInIteration = true;
					}
				}
			}
		}
	}

	return newState;
};

export const usePermissions = () => {
	const [permissions, _setPermissions] = useState<PermissionsState>(initialPermissionsState);

	const setPermissions = useCallback((state: PermissionsState) => {
		_setPermissions(sanitizePermissionsState(state));
	}, []);

	const updatePermission = (flag: PermissionFlag) => {
		const newState = { ...permissions };

		if (permissions[flag]) {
			newState[flag] = false;
		} else {
			newState[flag] = true;

			const queue: PermissionFlag[] = [flag];
			const processed = new Set<PermissionFlag>();

			while (queue.length > 0) {
				const currentFlag = queue.shift()!;
				if (processed.has(currentFlag)) continue;
				processed.add(currentFlag);

				const dependencies = permissionDependencies[currentFlag]?.requires || [];
				dependencies.forEach(reqFlag => {
					if (reqFlag in newState && !newState[reqFlag]) {
						newState[reqFlag] = true;
						if (!processed.has(reqFlag)) {
							queue.push(reqFlag);
						}
					}
				});
			}
		}
		setPermissions(newState);
	};

	const resetPermissions = () => {
		setPermissions(initialPermissionsState);
	};

	return { permissions, updatePermission, resetPermissions, setPermissions };
};
